import { useCallback, useEffect, useState } from 'react';
import { ReportsApiError, reportsApi } from '../services/reportsApi';
import type { BudgetActualReport, PeriodFilterType } from '../types/reporting.types';

interface UseReportDataResult {
  reportData: BudgetActualReport | null;
  isLoading: boolean;
  error: string | null;
  reload: () => Promise<void>;
}

const useReportData = (
  selectedYear: number,
  selectedPeriod: PeriodFilterType
): UseReportDataResult => {
  const [reportData, setReportData] = useState<BudgetActualReport | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadReportData = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      const data = await reportsApi.getBudgetActualReport({
        year: selectedYear,
        period_type: selectedPeriod,
      });
      setReportData(data);
    } catch (err) {
      if (err instanceof ReportsApiError) {
        setError(err.message);
      } else {
        setError('Failed to load report data. Please try again.');
      }
      console.error('Failed to load report data:', err);
    } finally {
      setIsLoading(false);
    }
  }, [selectedYear, selectedPeriod]);

  useEffect(() => {
    loadReportData();
  }, [loadReportData]);

  return { reportData, isLoading, error, reload: loadReportData };
};

export default useReportData;
